import './rating-compliance-rules.scss'

import { Button, Card, Select, Table, Typography } from 'antd'
import classnames from 'classnames'
import React, { useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetchDetailByDomain } from '../../../../api'
import { momentDateHandler } from '../../../../date/moment-date-handler'
import { CreateRatingComplianceRuleDrawer } from './CreateRatingComplianceRule/CreateRatingComplianceRuleDrawer'
import { DeleteRatingComplianceRuleModal } from './DeleteRatingComplianceRule/DeleteRatingComplianceRule'
import { RatingComplianceRule } from './models'
import { buildRatingComplianceRulesTableColumns } from './rating-compliance-rules-table-columns'
import { UpdateIssuerComplianceRuleDrawer } from './UpdateRatingComplianceRule'

const { formatDate, newDate } = momentDateHandler

interface RatingsComplianceRuleResponse {
  list: RatingComplianceRule[]
}

export const RatingComplianceRules = ({ tenant }) => {
  const [currentGroupId, setCurrentGroupId] = useState(null)
  const [ruleToDelete, setRuleToDelete] = useState(null)
  const [ruleToUpdate, setRuleToUpdate] = useState(null)
  const [isCreatingRule, setIsCreatingRule] = useState(false)

  const { data: ratingCompliancesGroups } = useSWR(
    tenant ? ['/compliances/ratings/groups', tenant] : null,
    fetchDetailByDomain,
  )

  const { data: ratingsCompliances, mutate } = useSWR<RatingsComplianceRuleResponse>(
    currentGroupId ? [`/compliances/ratings/groups/${currentGroupId}/rules`, tenant] : null,
    fetchDetailByDomain,
  )

  const groupOptions = ratingCompliancesGroups
    ? ratingCompliancesGroups.list.map(({ id, startDate }) => ({
        label: `Group ${id} starting at ${formatDate({ date: newDate(startDate) })}`,
        value: id,
      }))
    : []

  const onUpdate = (ratingComplianceRule: RatingComplianceRule) => setRuleToUpdate(ratingComplianceRule)

  const onDelete = (rule: RatingComplianceRule) => setRuleToDelete(rule)

  const onCloseDialogs = () => {
    setIsCreatingRule(null)
    setRuleToUpdate(null)
    setRuleToDelete(null)
  }

  const onSuccess = () => {
    onCloseDialogs()
    mutate()
  }

  const columns = buildRatingComplianceRulesTableColumns({ onUpdate, onDelete })

  return (
    <Column className="rating-compliance-rules">
      <Card loading={!ratingCompliancesGroups}>
        <Select options={groupOptions} placeholder="Select a rating compliance group" onSelect={setCurrentGroupId} />
      </Card>

      <Card
        title={<ComplianceTitle currentGroupId={currentGroupId} onCreate={setIsCreatingRule} />}
        loading={!ratingCompliancesGroups}
      >
        {!ratingsCompliances ? (
          <p>Please select a rating compliance group above</p>
        ) : (
          <Table
            rowClassName={(ratingComplianceRule: RatingComplianceRule) =>
              classnames({ active: ratingComplianceRule.id === (ruleToUpdate?.id || ruleToDelete?.id) })
            }
            dataSource={ratingsCompliances?.list}
            loading={!ratingsCompliances}
            columns={columns}
            pagination={false}
            rowKey="id"
            size="small"
          />
        )}
      </Card>

      <CreateRatingComplianceRuleDrawer
        domain={tenant}
        groupId={currentGroupId}
        isVisible={isCreatingRule}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <UpdateIssuerComplianceRuleDrawer
        domain={tenant}
        groupId={currentGroupId}
        ratingComplianceRule={ruleToUpdate}
        isVisible={currentGroupId && ruleToUpdate?.id}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />

      <DeleteRatingComplianceRuleModal
        domain={tenant}
        groupId={currentGroupId}
        rule={ruleToDelete}
        isVisible={ruleToDelete}
        onClose={onCloseDialogs}
        onSuccess={onSuccess}
      />
    </Column>
  )
}

const ComplianceTitle = ({ currentGroupId, onCreate }) => (
  <Row alignContentCenter justifyContentSpaceBetween>
    <Typography.Title level={3}>{`Rating Compliance ${
      currentGroupId ? 'for Group ' + currentGroupId : ''
    } `}</Typography.Title>
    {currentGroupId && (
      <Button type="primary" onClick={onCreate}>
        Create Rating Rule
      </Button>
    )}
  </Row>
)
